import { imageServiceProfiles, level1Support } from '@atlas-viewer/iiif-image-api';

const types = [
    'sc:Collection',
    'sc:Manifest',
    'sc:Canvas',
    'oa:AnnotationList',
    'oa:Annotation',
    'sc:Range',
    'sc:Layer',
    'sc:Sequence',
    'oa:Choice',
    'Service',
    'ContentResource',
];
function identifyResource(resource) {
    if (typeof resource === 'undefined' || resource === null) {
        throw new Error('Null or undefined is not a valid entity.');
    }
    if (Array.isArray(resource)) {
        throw new Error('Array is not a valid entity');
    }
    if (typeof resource !== 'object') {
        throw new Error(`${typeof resource} is not a valid entity`);
    }
    if (typeof resource['@type'] === 'string') {
        const hasType = types.indexOf(resource['@type']);
        if (hasType !== -1) {
            return types[hasType];
        }
    }
    if (resource.profile) {
        return 'Service';
    }
    if (resource.format) {
        return 'ContentResource';
    }
    if (resource['@type']) {
        return 'ContentResource';
    }
    throw new Error('Resource type is not known');
}
class Traverse {
    constructor(traversals, options = {}) {
        this.traversals = Object.assign({ collection: [], manifest: [], canvas: [], annotationList: [], sequence: [], annotation: [], contentResource: [], choice: [], range: [], service: [], layer: [] }, traversals);
        this.options = Object.assign({ convertPropsToArray: true, mergeMemberProperties: true, allowUndefinedReturn: false }, options);
    }
    static all(traversal) {
        return new Traverse({
            collection: [traversal],
            manifest: [traversal],
            canvas: [traversal],
            annotationList: [traversal],
            sequence: [traversal],
            annotation: [traversal],
            contentResource: [traversal],
            choice: [traversal],
            range: [traversal],
            service: [traversal],
            layer: [traversal],
        });
    }
    traverseCollection(collection) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traverseCollectionItems(collection))), this.traversals.collection);
    }
    traverseCollectionItems(collection) {
        if (this.options.mergeMemberProperties) {
            const members = [
                ...(collection.manifests || []).map(manifest => {
                    if (typeof manifest === 'string') {
                        return { '@id': manifest, '@type': 'sc:Manifest' };
                    }
                    return manifest;
                }),
                ...(collection.collections || []).map(subCollection => {
                    if (typeof subCollection === 'string') {
                        return { '@id': subCollection, '@type': 'sc:Collection' };
                    }
                    return subCollection;
                }),
                ...(collection.members || []),
            ];
            delete collection.collections;
            delete collection.manifests;
            collection.members = members;
        }
        if (collection.manifests) {
            collection.manifests = collection.manifests.map(manifest => this.traverseManifest(typeof manifest === 'string'
                ? { '@id': manifest, '@type': 'sc:Manifest' }
                : manifest));
        }
        if (collection.collections) {
            collection.collections = collection.collections.map(subCollection => this.traverseCollection(typeof subCollection === 'string'
                ? { '@id': subCollection, '@type': 'sc:Collection' }
                : subCollection));
        }
        if (collection.members) {
            collection.members = collection.members.map(member => {
                if (typeof member === 'string') {
                    return member;
                }
                return this.traverseUnknown(member);
            });
        }
        return collection;
    }
    traverseManifest(manifest) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traverseManifestItems(manifest))), this.traversals.manifest);
    }
    traverseManifestItems(manifest) {
        if (manifest.sequences) {
            manifest.sequences = manifest.sequences.map(sequence => this.traverseSequence(sequence));
        }
        if (manifest.structures) {
            manifest.structures = manifest.structures.map(structure => this.traverseRange(structure));
        }
        return manifest;
    }
    traverseSequence(sequence) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traverseSequenceItems(sequence))), this.traversals.sequence);
    }
    traverseSequenceItems(sequence) {
        if (sequence.canvases) {
            sequence.canvases = sequence.canvases.map(canvas => this.traverseCanvas(canvas));
        }
        return sequence;
    }
    traverseCanvas(canvas) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traverseCanvasItems(canvas))), this.traversals.canvas);
    }
    traverseCanvasItems(canvas) {
        if (canvas.images) {
            canvas.images = canvas.images.map(image => this.traverseAnnotation(image));
        }
        if (canvas.otherContent) {
            canvas.otherContent = canvas.otherContent.map(annotationList => this.traverseAnnotationList(annotationList));
        }
        return canvas;
    }
    traverseRange(range) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traverseRangeItems(range))), this.traversals.range);
    }
    traverseRangeItems(range) {
        if (this.options.mergeMemberProperties) {
            const members = [
                ...(range.ranges || []).map((innerRange) => {
                    if (typeof innerRange === 'string') {
                        return { '@id': innerRange, '@type': 'sc:Range' };
                    }
                    return innerRange;
                }),
                ...(range.canvases || []).map((canvas) => {
                    if (typeof canvas === 'string') {
                        return { '@id': canvas, '@type': 'sc:Canvas' };
                    }
                    return canvas;
                }),
                ...(range.members || []),
            ];
            delete range.ranges;
            delete range.canvases;
            range.members = members.length ? members.map(member => this.traverseUnknown(member)) : undefined;
        }
        return range;
    }
    traverseAnnotationList(annotationList) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traverseAnnotationListItems(annotationList))), this.traversals.annotationList);
    }
    traverseAnnotationListItems(annotationList) {
        if (annotationList.resources) {
            annotationList.resources = annotationList.resources.map(annotation => this.traverseAnnotation(annotation));
        }
        return annotationList;
    }
    traverseAnnotation(annotation) {
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(this.traverseAnnotationItems(annotation))), this.traversals.annotation);
    }
    traverseAnnotationItems(annotation) {
        if (annotation.resource) {
            annotation.resource = this.traverseContentResource(annotation.resource);
        }
        if (annotation.on) ;
        return annotation;
    }
    traverseLayer(layer) {
        return this.traverseType(this.traverseLinking(this.traverseLayerItems(layer)), this.traversals.layer);
    }
    traverseLayerItems(layer) {
        if (layer.otherContent) {
            layer.otherContent = layer.otherContent.map(annotationList => this.traverseAnnotationList(annotationList));
        }
        return layer;
    }
    traverseChoice(choice) {
        return this.traverseType(this.traverseChoiceItems(choice), this.traversals.choice);
    }
    traverseChoiceItems(choice) {
        if (choice.default && choice.default !== 'rdf:nil') {
            choice.default = this.traverseContentResource(choice.default);
        }
        if (choice.item && choice.item !== 'rdf:nil') {
            choice.item = choice.item.map(item => this.traverseContentResource(item));
        }
        return choice;
    }
    traverseService(service) {
        return this.traverseType(this.traverseLinking(service), this.traversals.service);
    }
    traverseContentResource(contentResource) {
        if (contentResource['@type'] === 'oa:Choice') {
            return this.traverseChoice(contentResource);
        }
        return this.traverseType(this.traverseDescriptive(this.traverseLinking(contentResource)), this.traversals.contentResource);
    }
    traverseUnknown(item) {
        if (!item['@type'] || typeof item === 'string') {
            return item;
        }
        switch (identifyResource(item)) {
            case 'sc:Collection':
                return this.traverseCollection(item);
            case 'sc:Manifest':
                return this.traverseManifest(item);
            case 'sc:Canvas':
                return this.traverseCanvas(item);
            case 'sc:Sequence':
                return this.traverseSequence(item);
            case 'sc:Range':
                return this.traverseRange(item);
            case 'oa:Annotation':
                return this.traverseAnnotation(item);
            case 'oa:AnnotationList':
                return this.traverseAnnotationList(item);
            case 'sc:Layer':
                return this.traverseLayer(item);
            case 'Service':
                return this.traverseService(item);
            case 'oa:Choice':
                return this.traverseChoice(item);
            case 'ContentResource':
                return this.traverseContentResource(item);
        }
        if (item.profile) {
            return this.traverseService(item);
        }
        return item;
    }
    traverseImageResource(contentResource) {
        const wasArray = Array.isArray(contentResource);
        const resourceList = Array.isArray(contentResource) ? contentResource : [contentResource];
        const newResourceList = [];
        for (const singleResource of resourceList) {
            if (typeof singleResource === 'string') {
                newResourceList.push(this.traverseContentResource({ '@id': singleResource, '@type': 'dctypes:Image' }));
            }
            else {
                newResourceList.push(this.traverseContentResource(singleResource));
            }
        }
        if (!wasArray && !this.options.convertPropsToArray) {
            return newResourceList[0];
        }
        return newResourceList;
    }
    traverseDescriptive(resource) {
        if (resource.thumbnail) {
            resource.thumbnail = this.traverseImageResource(resource.thumbnail);
        }
        if (resource.logo) {
            resource.logo = this.traverseImageResource(resource.logo);
        }
        return resource;
    }
    traverseOneOrMoreServices(allServices) {
        const wasArray = Array.isArray(allServices);
        const services = Array.isArray(allServices) ? allServices : [allServices];
        const newServices = [];
        for (const service of services) {
            newServices.push(this.traverseService(service));
        }
        if (!wasArray && !this.options.convertPropsToArray) {
            return newServices[0];
        }
        return newServices;
    }
    traverseLinking(resource) {
        if (resource.related) {
            resource.related = this.traverseOneOrManyType(resource.related, this.traversals.contentResource);
        }
        if (resource.rendering) {
            resource.rendering = this.traverseOneOrManyType(resource.rendering, this.traversals.contentResource);
        }
        if (resource.service) {
            resource.service = this.traverseOneOrMoreServices(resource.service);
        }
        if (resource.seeAlso) {
            resource.seeAlso = this.traverseOneOrManyType(resource.seeAlso, this.traversals.contentResource);
        }
        if (resource.within) {
            if (typeof resource.within === 'string') ;
            else {
                resource.within = this.traverseOneOrManyType(resource.within, this.traversals.contentResource);
            }
        }
        if (resource.startCanvas) {
            if (typeof resource.startCanvas === 'string') {
                resource.startCanvas = this.traverseType({ '@id': resource.startCanvas, '@type': 'sc:Canvas' }, this.traversals.canvas);
            }
            else if (resource.startCanvas) {
                this.traverseType(resource.startCanvas, this.traversals.canvas);
            }
        }
        if (resource.contentLayer) {
            if (typeof resource.contentLayer === 'string') {
                resource.contentLayer = this.traverseLayer({ '@id': resource.contentLayer, '@type': 'sc:Layer' });
            }
            else {
                resource.contentLayer = this.traverseLayer(resource.contentLayer);
            }
        }
        return resource;
    }
    traverseOneOrManyType(object, traversals) {
        if (!Array.isArray(object)) {
            if (this.options.convertPropsToArray) {
                object = [object];
            }
            else {
                return this.traverseType(object, traversals);
            }
        }
        return object.map(singleObj => this.traverseType(singleObj, traversals));
    }
    traverseType(object, traversals) {
        return traversals.reduce((acc, traversal) => {
            const returnValue = traversal(acc);
            if (typeof returnValue === 'undefined' && !this.options.allowUndefinedReturn) {
                return acc;
            }
            return returnValue;
        }, object);
    }
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const configuration = {
    attributionLabel: 'Attribution',
    lang: 'none',
    providerId: 'http://example.org/provider',
    providerName: 'Unknown',
};
function convertLanguageMapping(inputLangProperty, defaultLang = 'none') {
    if (!inputLangProperty) {
        return {};
    }
    const arrayOfValues = Array.isArray(inputLangProperty) ? inputLangProperty : [inputLangProperty];
    const languageMap = {};
    for (const language of arrayOfValues) {
        if (typeof language === 'string') {
            languageMap[defaultLang] = languageMap[defaultLang] ? languageMap[defaultLang] : [];
            languageMap[defaultLang].push(language || '');
            continue;
        }
        if (!language['@language']) {
            languageMap[defaultLang] = languageMap[defaultLang] ? languageMap[defaultLang] : [];
            languageMap[defaultLang].push(language['@value'] || '');
            continue;
        }
        const lang = language['@language'];
        languageMap[lang] = languageMap[lang] ? languageMap[lang] : [];
        languageMap[lang].push(language['@value'] || '');
    }
    return languageMap;
}
function getProfile(profile) {
    if (Array.isArray(profile)) {
        return getProfile(profile.find(s => typeof s === 'string'));
    }
    if (imageServiceProfiles.indexOf(profile) !== -1) {
        return 'level2';
    }
    if (level1Support.indexOf(profile) !== -1) {
        return 'level1';
    }
    if (typeof profile !== 'string') {
        return undefined;
    }
    return profile;
}
function getTypeFromContext(inputContexts) {
    const contexts = Array.isArray(inputContexts) ? inputContexts : [inputContexts];
    for (const context of contexts) {
        switch (context) {
            case 'http://iiif.io/api/image/2/context.json':
            case 'http://library.stanford.edu/iiif/image-api/1.1/compliance.html#level2':
                return 'ImageService2';
            case 'http://iiif.io/api/image/1/context.json':
            case 'http://library.stanford.edu/iiif/image-api/1.1/context.json':
                return 'ImageService1';
            case 'http://iiif.io/api/annex/openannotation/context.json':
                return 'ImageApiSelector';
        }
    }
    return undefined;
}
function getTypeFromProfile(inputProfile) {
    switch (inputProfile) {
        case 'http://iiif.io/api/image/2/level0.json':
        case 'http://iiif.io/api/image/2/level1.json':
        case 'http://iiif.io/api/image/2/level2.json':
            return 'ImageService2';
        case 'http://iiif.io/api/auth/1/kiosk':
        case 'http://iiif.io/api/auth/1/login':
        case 'http://iiif.io/api/auth/1/clickthrough':
        case 'http://iiif.io/api/auth/1/external':
        case 'http://iiif.io/api/auth/0/kiosk':
        case 'http://iiif.io/api/auth/0/login':
        case 'http://iiif.io/api/auth/0/clickthrough':
        case 'http://iiif.io/api/auth/0/external':
            return 'AuthCookieService1';
        case 'http://iiif.io/api/auth/1/token':
        case 'http://iiif.io/api/auth/0/token':
            return 'AuthTokenService1';
        case 'http://iiif.io/api/auth/1/logout':
        case 'http://iiif.io/api/auth/0/logout':
            return 'AuthLogoutService1';
        case 'http://iiif.io/api/search/1/search':
        case 'http://iiif.io/api/search/0/search':
            return 'SearchService1';
        case 'http://iiif.io/api/search/1/autocomplete':
        case 'http://iiif.io/api/search/0/autocomplete':
            return 'AutoCompleteService1';
    }
    return undefined;
}
function ensureArray(maybeArray) {
    if (Array.isArray(maybeArray)) {
        return maybeArray;
    }
    return [maybeArray];
}
function removePrefix(str) {
    for (const prefix of ['sc', 'oa', 'dcterms', 'dctypes', 'iiif']) {
        if (str.startsWith(`${prefix}:`)) {
            return str.slice(prefix.length + 1);
        }
    }
    return str;
}
function getNewType(resource) {
    const id = resource['@id'] || resource.id;
    let oldType = resource['@type'] || resource.type;
    const profile = resource.profile || undefined;
    const context = resource['@context'] || undefined;
    if (profile) {
        const possibleType = getTypeFromProfile(profile);
        if (possibleType) {
            return possibleType;
        }
    }
    if (context) {
        const possibleType = getTypeFromContext(context);
        if (possibleType) {
            return possibleType;
        }
    }
    if (oldType) {
        if (Array.isArray(oldType)) {
            if (oldType.indexOf('oa:CssStylesheet') !== -1) {
                return 'CssStylesheet';
            }
            if (oldType.indexOf('cnt:ContentAsText') !== -1) {
                return 'TextualBody';
            }
            oldType = oldType[0];
        }
        for (const prefix of ['sc', 'oa', 'dcterms', 'dctypes', 'iiif']) {
            if (oldType.startsWith(`${prefix}:`)) {
                oldType = oldType.slice(prefix.length + 1);
                break;
            }
        }
        switch (oldType) {
            case 'Layer':
                return 'AnnotationCollection';
            case 'AnnotationList':
                return 'AnnotationPage';
            case 'cnt:ContentAsText':
                return 'TextualBody';
        }
    }
    if (resource.format) {
        if (resource.format.startsWith('image/')) {
            return 'Image';
        }
        if (resource.format.startsWith('text/')) {
            return 'Text';
        }
        if (resource.format === 'application/pdf') {
            return 'Text';
        }
        if (resource.format.startsWith('application/')) {
            return 'Dataset';
        }
    }
    if (id && (id.endsWith('.jpg') || id.endsWith('.png') || id.endsWith('.jpeg'))) {
        return 'Image';
    }
    if (!oldType) {
        return 'unknown';
    }
    return oldType;
}
const licenseRegex = /http(s)?:\/\/(creativecommons.org|rightsstatements.org)[^"'\\<\n]+/gm;
function extractLicense(license) {
    const matches = license.match(licenseRegex);
    if (matches) {
        return matches[0];
    }
    return license;
}
function fixLicense(license, licenseLabel = 'Rights/License', lang = 'none') {
    let rights = null;
    const metadata = [];
    const licenseList = Array.isArray(license) ? license : [license];
    for (const rawLicense of licenseList) {
        const singleLicense = rawLicense ? extractLicense(rawLicense) : undefined;
        if (singleLicense &&
            (singleLicense.indexOf('creativecommons.org') !== -1 || singleLicense.indexOf('rightsstatements.org') !== -1)) {
            if (singleLicense.startsWith('https://')) {
                rights = `http://${singleLicense.slice(8)}`;
            }
            else {
                rights = singleLicense;
            }
            continue;
        }
        if (singleLicense) {
            metadata.push({
                label: { [lang]: [licenseLabel] },
                value: { [lang]: [singleLicense] },
            });
        }
    }
    return [rights, metadata];
}
const removeContexts = [
    'http://iiif.io/api/presentation/2/context.json',
    'http://iiif.io/api/image/2/context.json',
    'http://iiif.io/api/image/1/context.json',
    'http://library.stanford.edu/iiif/image-api/1.1/context.json',
    'http://iiif.io/api/search/1/context.json',
    'http://iiif.io/api/search/0/context.json',
    'http://iiif.io/api/auth/1/context.json',
    'http://iiif.io/api/auth/0/context.json',
    'http://iiif.io/api/annex/openannotation/context.json',
];
function fixContext(inputContext) {
    if (inputContext) {
        const contexts = Array.isArray(inputContext) ? inputContext : [inputContext];
        const newContexts = [];
        for (const context of contexts) {
            if (context === 'http://iiif.io/api/presentation/2/context.json') {
                newContexts.push('http://iiif.io/api/presentation/3/context.json');
            }
            if (removeContexts.indexOf(context) !== -1) {
                continue;
            }
            newContexts.push(context);
        }
        if (contexts.length) {
            return newContexts.length === 1 ? newContexts[0] : newContexts;
        }
    }
    return undefined;
}
function convertMetadata(metadata) {
    if (!metadata) {
        return [];
    }
    return metadata.map((item) => {
        return {
            label: convertLanguageMapping(item.label),
            value: convertLanguageMapping(item.value),
        };
    });
}
function removeUndefinedProperties(obj) {
    for (const prop in obj) {
        if (typeof obj[prop] === 'undefined' || obj[prop] === null) {
            delete obj[prop];
        }
    }
    return obj;
}
let mintedIdCounter = 0;
function mintNewIdFromResource(resource, subresource) {
    const origId = encodeURI(resource.id || resource['@id'] || '').trim();
    if (origId && subresource) {
        return `${origId}/${subresource}`;
    }
    if (origId) {
        return origId;
    }
    mintedIdCounter++;
    return `http://example.org/${resource['@type']}${subresource ? `/${subresource}` : ''}/${mintedIdCounter}`;
}
function technicalProperties(resource) {
    const allBehaviours = [...(resource.behavior || [])];
    if (resource.viewingHint) {
        allBehaviours.push(resource.viewingHint);
    }
    return {
        '@context': resource['@context'] ? fixContext(resource['@context']) : undefined,
        id: (resource['@id'] || mintNewIdFromResource(resource)).trim(),
        type: getNewType(resource),
        behavior: allBehaviours.length ? allBehaviours : undefined,
        height: resource.height ? resource.height : undefined,
        width: resource.width ? resource.width : undefined,
        motivation: resource.motivation ? removePrefix(resource.motivation) : undefined,
        viewingDirection: resource.viewingDirection,
        profile: resource.profile,
        format: resource.format ? resource.format : undefined,
        duration: undefined,
        timeMode: undefined,
    };
}
function descriptiveProperties(resource) {
    const [rights, extraMetadata] = fixLicense(resource.license);
    const allMetadata = [...(resource.metadata ? convertMetadata(resource.metadata) : []), ...extraMetadata];
    return {
        rights,
        metadata: allMetadata.length ? allMetadata : undefined,
        label: resource.label ? convertLanguageMapping(resource.label) : undefined,
        requiredStatement: resource.attribution
            ? {
                label: convertLanguageMapping(configuration.attributionLabel),
                value: convertLanguageMapping(resource.attribution),
            }
            : undefined,
        navDate: resource.navDate,
        summary: resource.description ? convertLanguageMapping(resource.description) : undefined,
        thumbnail: resource.thumbnail,
    };
}
function parseWithin(resource) {
    if (!resource.within) {
        return undefined;
    }
    const withinProperties = Array.isArray(resource.within) ? resource.within : [resource.within];
    const returnPartOf = [];
    for (const within of withinProperties) {
        if (typeof within === 'string') {
            if (within) {
                switch (resource['@type']) {
                    case 'sc:Manifest':
                        returnPartOf.push({ id: within, type: 'Collection' });
                        break;
                }
            }
        }
        else if (within['@id']) {
            returnPartOf.push({
                id: within['@id'],
                type: getNewType(within),
            });
        }
        else ;
    }
    return returnPartOf.length ? returnPartOf : undefined;
}
function linkingProperties(resource) {
    const related = resource.related ? (Array.isArray(resource.related) ? resource.related : [resource.related]) : [];
    const layer = resource.contentLayer;
    return {
        provider: resource.logo || related.length
            ? [
                {
                    id: configuration.providerId,
                    type: 'Agent',
                    homepage: related.length ? [related[0]] : undefined,
                    logo: resource.logo ? (Array.isArray(resource.logo) ? resource.logo : [resource.logo]) : undefined,
                    label: convertLanguageMapping(configuration.providerName),
                },
            ]
            : undefined,
        partOf: parseWithin(resource),
        rendering: resource.rendering,
        seeAlso: resource.seeAlso,
        start: resource.startCanvas,
        service: resource.service ? ensureArray(resource.service) : undefined,
        supplementary: layer ? [layer] : undefined,
    };
}
function upgradeCollection(collection) {
    return removeUndefinedProperties(Object.assign(Object.assign(Object.assign(Object.assign({}, technicalProperties(collection)), descriptiveProperties(collection)), linkingProperties(collection)), { items: collection.members }));
}
function upgradeManifest(manifest) {
    const allCanvases = [];
    const behavior = [];
    for (const sequence of manifest.sequences || []) {
        if (sequence.canvases.length) {
            allCanvases.push(...sequence.canvases);
        }
        if (sequence.behavior) {
            behavior.push(...sequence.behavior);
        }
    }
    const technical = technicalProperties(manifest);
    if (behavior.length) {
        if (technical.behavior) {
            technical.behavior.push(...behavior);
        }
        else {
            technical.behavior = behavior;
        }
    }
    return removeUndefinedProperties(Object.assign(Object.assign(Object.assign(Object.assign({}, technical), descriptiveProperties(manifest)), linkingProperties(manifest)), { items: allCanvases, structures: manifest.structures }));
}
function upgradeCanvas(canvas) {
    return removeUndefinedProperties(Object.assign(Object.assign(Object.assign(Object.assign({}, technicalProperties(canvas)), descriptiveProperties(canvas)), linkingProperties(canvas)), { items: canvas.images && canvas.images.length
            ? [
                {
                    id: mintNewIdFromResource(canvas, 'annotation-page'),
                    type: 'AnnotationPage',
                    items: canvas.images,
                },
            ]
            : undefined }));
}
function upgradeAnnotationList(annotationPage) {
    return removeUndefinedProperties(Object.assign(Object.assign(Object.assign(Object.assign({}, technicalProperties(annotationPage)), descriptiveProperties(annotationPage)), linkingProperties(annotationPage)), { items: annotationPage.resources }));
}
function upgradeSequence(sequence) {
    if (!sequence.canvases || sequence.canvases.length === 0) {
        return {
            canvases: [],
            behavior: [],
        };
    }
    return {
        canvases: sequence.canvases,
        behavior: sequence.viewingHint ? [sequence.viewingHint] : [],
    };
}
function upgradeAnnotation(annotation) {
    return removeUndefinedProperties(Object.assign(Object.assign(Object.assign(Object.assign({}, technicalProperties(annotation)), descriptiveProperties(annotation)), linkingProperties(annotation)), { target: typeof annotation.on === 'string' ? encodeURI(annotation.on).trim() : annotation.on, body: annotation.resource }));
}
function upgradeContentResource(inputContentResource) {
    const contentResource = inputContentResource;
    return removeUndefinedProperties(Object.assign(Object.assign(Object.assign({}, technicalProperties(contentResource)), descriptiveProperties(contentResource)), linkingProperties(contentResource)));
}
function upgradeChoice(choice) {
    const items = [];
    if (choice.default && choice.default !== 'rdf:nil') {
        items.push(choice.default);
    }
    if (choice.item && choice.item !== 'rdf:nil') {
        items.push(...choice.item);
    }
    return Object.assign(Object.assign(Object.assign({}, technicalProperties(choice)), descriptiveProperties(choice)), { items: items });
}
function upgradeRange(range) {
    return removeUndefinedProperties(Object.assign(Object.assign(Object.assign(Object.assign({}, technicalProperties(range)), descriptiveProperties(range)), linkingProperties(range)), { items: range.members }));
}
function upgradeService(service) {
    const _a = service, { '@id': id, '@type': type, '@context': context, profile } = _a, newService = __rest(_a, ['@id', '@type', '@context', "profile"]);
    if (id) {
        newService.id = id;
    }
    newService.type = getNewType(service);
    if (newService.type === 'unknown') {
        newService.type = 'Service';
    }
    if (profile) {
        newService.profile = getProfile(profile);
    }
    return removeUndefinedProperties(Object.assign(Object.assign({}, newService), descriptiveProperties(newService)));
}
function upgradeLayer(layer) {
    return removeUndefinedProperties(Object.assign(Object.assign(Object.assign({}, technicalProperties(layer)), descriptiveProperties(layer)), linkingProperties(layer)));
}
const presentation2to3 = new Traverse({
    collection: [upgradeCollection],
    manifest: [upgradeManifest],
    canvas: [upgradeCanvas],
    annotationList: [upgradeAnnotationList],
    sequence: [upgradeSequence],
    annotation: [upgradeAnnotation],
    contentResource: [upgradeContentResource],
    choice: [upgradeChoice],
    range: [upgradeRange],
    service: [upgradeService],
    layer: [upgradeLayer],
});
function convertPresentation2(entity) {
    if ((entity &&
        entity['@context'] &&
        (entity['@context'] === 'http://iiif.io/api/presentation/2/context.json' ||
            entity['@context'].indexOf('http://iiif.io/api/presentation/2/context.json') !== -1 ||
            entity['@context'] === 'http://www.shared-canvas.org/ns/context.json')) ||
        entity['@context'] === 'http://iiif.io/api/image/2/context.json') {
        return presentation2to3.traverseUnknown(entity);
    }
    return entity;
}

export { Traverse, convertLanguageMapping, convertPresentation2, getProfile, getTypeFromContext, identifyResource, presentation2to3, types };
//# sourceMappingURL=index.es.js.map
